<template>
    <div class="modal-container fixed inset-0 z-50 bg-black bg-opacity-30 flex items-center justify-center">
        <div class="modal-body bg-white shadow-xl m-3 max-h-[90%] w-full max-w-[850px] min-w-[600px] flex flex-col overflow-hidden">
            <!-- Close button -->
            <button @click="closeDialog" class="modal-close p-2 self-end">
                <XMarkIcon class="h-5 w-5 text-gray-600 hover:text-cyan-900" />
            </button>

            <div class="flex justify-between items-center bg-gray-100 px-4 py-2">
                <!-- Left actions -->
                <div class="flex items-center space-x-4">
                    <button
                        @click="copyToClipboard"
                        :disabled="copied"
                        class="flex items-center space-x-1 text-gray-700 hover:text-gray-900 disabled:opacity-50">
                        <component :is="copied ? ClipboardDocumentCheckIcon : ClipboardIcon" class="h-5 w-5" />
                        <span>{{ copied ? 'Copied!' : 'Copy raw message' }}</span>
                    </button>

                    <button
                        v-if="showAiExplain"
                        @click="explain"
                        :disabled="loading"
                        class="flex items-center space-x-2 bg-green-500 text-white px-3 py-1 rounded-lg hover:bg-green-600 disabled:opacity-50">
                        <img src="/icons/streamkap_glitter_white.svg" alt="Streamkap Logo" class="h-7 w-7" />
                        <span>Explain using Streamkap AI</span>
                        <ArrowPathIcon v-if="loading" class="h-5 w-5 animate-spin" />
                    </button>
                </div>
            </div>

            <!-- Scrollable content container -->
            <div class="flex-1 overflow-y-auto">
                <!-- Original Log Message -->
                <div class="mt-4 rounded-lg shadow-md">
                    <!-- optional log-level header -->
                    <div v-if="logLevel" class="flex items-center space-x-2 bg-gray-700 px-3 py-1">
                        <span class="text-gray-400 text-sm">{{ logLevel }}</span>
                    </div>

                    <!-- terminal output: constrained height with scroll -->
                    <pre
                        :class="[
                            isError ? 'text-red-400' : 'text-green-400',
                            'whitespace-pre-wrap break-words text-left text-sm font-mono bg-gray-900 px-4 py-3 overflow-y-auto max-h-[40vh]'
                        ]"
                        >{{ message }}</pre>
                </div>

                <!-- AI Summary -->
                <div v-if="hasAiData" class="bg-white rounded-lg shadow-md relative p-6 mt-4">
                    <h2 class="text-xl font-bold text-gray-800 shadow-b">
                        <span class="flex items-center w-full justify-center pb-2">
                            <img src="/icons/streamkap_glitter.svg" alt="Streamkap Logo" class="h-7 w-7 mr-1" />
                            Streamkap AI – Log Explained
                        </span>
                    </h2>
                    <div class="overflow-y-auto overflow-x-hidden whitespace-pre-wrap break-words text-left text-sm" style="max-height: 35vh">
                        <!-- vertical line -->
                        <div class="absolute left-8 top-6 bottom-6 w-px bg-gray-200"></div>

                        <!-- Summary -->
                        <div v-if="summaryData.summary" class="relative flex items-start mb-8">
                            <div class="ml-6">
                                <h3 class="text-lg font-semibold text-gray-800 mb-2 flex items-center space-x-2">
                                    <InformationCircleIcon class="h-5 w-5 text-cyan-600" />
                                    <span>Summary</span>
                                </h3>
                                <div v-html="sanitizedSummary" class="prose prose-sm text-gray-700"></div>
                            </div>
                        </div>

                        <!-- Recommended Actions -->
                        <div v-if="summaryData.recommended_actions" class="relative flex items-start mb-8">
                            <div class="ml-6">
                                <h3 class="text-lg font-semibold text-gray-800 mb-2 flex items-center space-x-2">
                                    <CheckBadgeIcon class="h-5 w-5 text-yellow-600" />
                                    <span>Recommended actions</span>
                                </h3>
                                <div v-html="sanitizedRecommendedActions" class="prose prose-sm text-gray-700"></div>
                            </div>
                        </div>

                        <!-- Context -->
                        <div v-if="summaryData.context" class="relative flex items-start mb-4">
                            <div class="ml-6">
                                <h3 class="text-lg font-semibold text-gray-800 mb-2 flex items-center space-x-2">
                                    <LightBulbIcon class="h-5 w-5 text-green-600" />
                                    <span>Context</span>
                                </h3>
                                <div v-html="sanitizedContext" class="prose prose-sm text-gray-700"></div>
                            </div>
                        </div>
                    </div>
                    <!-- ————— Disclaimer ————— -->
                    <div class="px-6 py-2 text-xs text-gray-500">Streamkap AI can make mistakes. Please verify the information.</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useLogsStore } from '../stores/logs.store';
import DOMPurify from 'dompurify';
import {
    XMarkIcon,
    ClipboardDocumentCheckIcon,
    ClipboardIcon,
    ArrowPathIcon,
    InformationCircleIcon,
    LightBulbIcon,
    CheckBadgeIcon
} from '@heroicons/vue/24/outline';

const props = defineProps({
    message: { type: String, required: true },
    isError: { type: Boolean, default: false },
    showAiExplain: { type: Boolean, default: false },
    connectorName: { type: String, required: false },
    namespaceName: { type: String, required: false },
    logLevel: { type: String, required: false },
    timestamp: { type: String, required: false },
    logSection: { type: String, required: false },
    entityType: { type: String, required: false },
    entityId: { type: String, required: false },
    logUuid: { type: String, required: false },
    // Now an object with keys: summary, context, recommended_actions (HTML string)
    summary: {
        type: Object,
        default: () => ({ summary: '', context: '', recommended_actions: '' })
    }
});

const emit = defineEmits(['cancel']);
const logsStore = useLogsStore();

const copied = ref(false);
const loading = ref(false);
const summaryData = ref({ summary: '', context: '', recommended_actions: '' });

// Determine if we have any AI data to show
const hasAiData = computed(() => !!summaryData.value.context || !!summaryData.value.summary || !!summaryData.value.recommended_actions);

// Safely sanitize HTML content
const sanitizedSummary = computed(() => DOMPurify.sanitize(summaryData.value.summary || ''));
const sanitizedRecommendedActions = computed(() => DOMPurify.sanitize(summaryData.value.recommended_actions || ''));
const sanitizedContext = computed(() => DOMPurify.sanitize(summaryData.value.context || ''));

// Initialise from existing prop
onMounted(() => {
    summaryData.value = {
        summary: props.summary.summary || '',
        context: props.summary.context || '',
        recommended_actions: props.summary.recommended_actions || ''
    };
});

function closeDialog() {
    emit('cancel');
}

async function copyToClipboard() {
    copied.value = true;
    await navigator.clipboard.writeText(props.message);
    setTimeout(() => {
        copied.value = false;
    }, 3000);
}

async function explain() {
    loading.value = true;
    // clear previous
    summaryData.value = { summary: '', context: '', recommended_actions: '' };

    try {
        // compute snippet length
        // Call your store action
        let charsToCut = 31;

        if (props.message.length < 31) {
            charsToCut = props.message.length;
        }

        if (props.message.length > 100) {
            charsToCut = 100;
        }

        if (props.message.length > 150) {
            charsToCut = 150;
        }

        if (props.message.length > 200) {
            charsToCut = 200;
        }

        const result = await logsStore.summarize({
            connector_name: props.connectorName,
            namespace_name: props.namespaceName,
            timestamp: props.timestamp,
            log_section: props.message.substring(0, charsToCut),
            entity_type: props.entityType,
            entity_id: props.entityId,
            log_uuid: props.logUuid
        });

        // assign returned object
        summaryData.value = {
            summary: result.summary || '',
            context: result.context || '',
            recommended_actions: result.recommended_actions || ''
        };
    } catch (err) {
        console.error('AI summary failed', err);
    } finally {
        loading.value = false;
    }
}
</script>

<style>
.modal-container {
    position: fixed;
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 100%;
    background-color: #cececeb5;
}
.modal-body {
    background-color: #fff;
    border-radius: 10px;
    padding: 20px 40px;
    min-width: 600px;
    max-width: 850px;
    display: flex;
    flex-direction: column;
}
.modal-close {
    cursor: pointer;
    position: relative;
    align-self: end;
    right: -25px;
    top: -10px;
}

.prose pre {
    white-space: pre-wrap; /* honour line breaks but allow wrapping */
    word-break: break-word; /* wrap long words/lines */
    overflow-x: hidden; /* no horizontal scroll */
}
</style>
